import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useLang } from '../context/LangContext'
import qaraaLogo from '../images/qaraalogo.webp'
import qaraaCoffee from '../images/qaraacoffee.webp'

let cachedOrders = null;
let cachedExpirations = null;

export default function History({ customer }) {
  const navigate = useNavigate()
  const { lang } = useLang()
  const [activeTab, setActiveTab] = useState('purchases')
  const [orders, setOrders] = useState(cachedOrders || [])
  const [expirations, setExpirations] = useState(cachedExpirations || [])
  const [loading, setLoading] = useState(!cachedOrders)

  const monthsKk = ['қаңтар', 'ақпан', 'наурыз', 'сәуір', 'мамыр', 'маусым', 'шілде', 'тамыз', 'қыркүйек', 'қазан', 'қараша', 'желтоқсан']
  const monthsRu = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
  const monthsEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

  const getMonthName = (m) => {
    if (lang === 'kk') return monthsKk[m]
    if (lang === 'en') return monthsEn[m]
    return monthsRu[m]
  }

  const t = {
    title: { kk: 'Операциялар', ru: 'История', en: 'History' }[lang],
    purchases: { kk: 'Сатып алынғандар', ru: 'Покупки', en: 'Purchases' }[lang],
    expiration: { kk: 'Жақында өшетін бонустар', ru: 'Сгорание бонусов', en: 'Bonus Expiration' }[lang],
    purchaseAmount: { kk: 'Жалпы сома', ru: 'Сумма покупки', en: 'Purchase amount' }[lang],
    bonusUsed: { kk: 'Бонуспен төленді', ru: 'Оплачено бонусами', en: 'Paid with bonuses' }[lang],
    tenge: { kk: '₸', ru: '₸', en: '₸' }[lang],
    bonus: { kk: 'бонус', ru: 'бонус', en: 'bonus' }[lang],
    noHistory: { kk: 'Тарих бос', ru: 'История пуста', en: 'No history' }[lang],
    noExpiration: { kk: 'Жанып кететін бонустар жоқ', ru: 'Нет сгорающих бонусов', en: 'No expiring bonuses' }[lang],
    qaraaStore: { kk: 'qaraa.asia', ru: 'qaraa.asia', en: 'qaraa.asia' }[lang],
    qaraaCoffee: { kk: 'qaraa.coffee', ru: 'qaraa.coffee', en: 'qaraa.coffee' }[lang],
    storeSubtitle: { kk: 'Онлайн дүкен', ru: 'Онлайн магазин', en: 'Online store' }[lang],
    coffeeSubtitle: { kk: 'Кофехана', ru: 'Кофейня', en: 'Coffee shop' }[lang],
  }

  const expireSoonText = {
    kk: 'Кеш болмай тұрып, жұмсап үлгеріңіз',
    ru: 'Успейте потратить, пока не поздно',
    en: 'Spend before it\'s too late'
  }[lang]

  const termsTitle = {
    kk: 'Бонус бағдарламасының шарттары:',
    ru: 'Условия бонусной программы:',
    en: 'Bonus program terms:'
  }[lang]

  const termsList = [
    { kk: 'Әрбір саудадан 5% кэшбэк есептеледі', ru: 'Кэшбэк 5% начисляется на каждую покупку', en: '5% cashback is credited on every purchase' },
    { kk: 'Бонустар есептелген сәттен бастап 12 ай жарамды', ru: 'Бонусы действуют 12 месяцев с момента начисления', en: 'Bonuses are valid for 12 months from accrual' },
    { kk: 'Тапсырыс сомасының 50%-ына дейін бонустармен төлеуге болады', ru: 'Бонусами можно оплатить до 50% суммы заказа', en: 'Up to 50% of the order amount can be paid with bonuses' },
    { kk: 'Тауар қайтарылған жағдайда бонустар жойылады', ru: 'При возврате товара бонусы аннулируются', en: 'Bonuses are voided upon product return' },
    { kk: 'Бонустар ақшаға айырбасталмайды', ru: 'Бонусы не подлежат обмену на деньги', en: 'Bonuses are non-exchangeable for cash' },
  ]

  const bonusLabel = lang === 'kk' ? 'бонус' : 'бонусов'

  const renderExpiresText = (day, monthName, year) => {
    if (lang === 'kk') return `${day} ${monthName} ${year} жылы күйіп кетеді`
    if (lang === 'en') return `Expires on ${monthName} ${day}, ${year}`
    return `Сгорят ${day} ${monthName} ${year}`
  }

  useEffect(() => {
    if (customer?.id) {
      fetchOrders()
    }
  }, [customer?.id])

  useEffect(() => {
    if (!customer?.id) return
    const interval = setInterval(() => {
      fetchOrders()
    }, 5000)
    return () => clearInterval(interval)
  }, [customer?.id])

  const fetchOrders = async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false })

      if (ordersError) throw ordersError

      const { data: allLedgers, error: allLedgersError } = await supabase
        .from('bonuses_ledger')
        .select('amount, type, order_id, created_at, expires_at')
        .eq('customer_id', customer.id)
        .eq('status', 'active')
        .order('created_at', { ascending: true })

      if (allLedgersError) console.warn('Error fetching ledgers:', allLedgersError)
      
      const allDebits = allLedgers?.filter(l => l.type === 'debit') || []
      const credits = allLedgers?.filter(l => l.type === 'credit') || []
      let totalSpent = allDebits.reduce((sum, d) => sum + (d.amount || 0), 0)
      
      const activeExpirations = []
      for (const c of credits) {
        if (totalSpent >= c.amount) {
          totalSpent -= c.amount
        } else {
          const remaining = c.amount - totalSpent
          totalSpent = 0
          if (c.expires_at) {
            activeExpirations.push({ amount: remaining, expires_at: c.expires_at })
          }
        }
      }

      const groupedExpirations = activeExpirations.reduce((acc, curr) => {
        const date = new Date(curr.expires_at).toISOString().split('T')[0]
        acc[date] = (acc[date] || 0) + curr.amount
        return acc
      }, {})

      const sortedExpirations = Object.keys(groupedExpirations).map(date => ({
        expires_at: date,
        amount: groupedExpirations[date]
      })).sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at))
      
      cachedExpirations = sortedExpirations
      setExpirations(sortedExpirations)

      const ordersWithBonuses = (ordersData || []).map((order) => {
        const orderTime = new Date(order.created_at).getTime()
        const orderDebits = (allDebits || []).filter(d => {
          if (d.order_id == order.id) return true
          if (!d.order_id) {
            const debitTime = new Date(d.created_at).getTime()
            if (Math.abs(debitTime - orderTime) < 10000) return true
          }
          return false
        })
        const bonusUsed = orderDebits.reduce((sum, d) => sum + (d.amount || 0), 0)
        return { ...order, bonus_used: bonusUsed }
      })

      cachedOrders = ordersWithBonuses
      setOrders(ordersWithBonuses)
    } catch (error) {
      console.error('Error fetching orders:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  const getSourceLogo = (source) => {
    if (source === 'qaraa_store' || source === 'store') {
      return { logo: qaraaLogo, label: t.qaraaStore, subtitle: t.storeSubtitle, color: '#007AFF' }
    } else if (source === 'qaraa_coffee' || source === 'coffee') {
      return { logo: qaraaCoffee, label: t.qaraaCoffee, subtitle: t.coffeeSubtitle, color: '#DB7093' }
    }
    return { logo: qaraaLogo, label: 'qaraa', subtitle: t.storeSubtitle, color: '#007AFF' }
  }

  return (
    <div style={{
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      padding: '20px',
      paddingBottom: '100px',
      background: '#F7F6F2',
      minHeight: 'calc(100vh - 200px)'
    }}>
      <style>{`
        * {
          -webkit-user-select: none !important;
          user-select: none !important;
          -webkit-user-drag: none !important;
          user-drag: none !important;
          -webkit-touch-callout: none !important;
        }
        img {
          -webkit-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }
        .highlight-wave {
          position: relative;
          color: #007AFF;
          display: inline-block;
        }
        .highlight-wave::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -2px;
          width: 100%;
          height: 8px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M1,7 Q25,2 50,6 T99,4' fill='none' stroke='%23007AFF' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E");
          background-size: 100% 100%;
          background-repeat: no-repeat;
        }
      `}</style>

      <div style={{ marginBottom: '24px', paddingLeft: '4px' }}>
        <h2 style={{
          fontSize: '22px', fontWeight: '700', color: '#111',
          margin: '0 0 4px 0', letterSpacing: '-0.5px'
        }}>
          {t.title}
        </h2>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
        <div style={{
          display: 'flex', background: '#F4F3EF', borderRadius: 100,
          padding: 3, gap: 2
        }}>
          <button onClick={() => setActiveTab('purchases')} style={{
            padding: '9px 18px', fontSize: 13, whiteSpace: 'nowrap',
            fontWeight: activeTab === 'purchases' ? 500 : 400,
            color: activeTab === 'purchases' ? '#111' : '#9A9890',
            border: activeTab === 'purchases' ? '0.5px solid #E8E6DF' : 'none',
            background: activeTab === 'purchases' ? '#fff' : 'transparent',
            borderRadius: 100, cursor: 'pointer', letterSpacing: '-0.1px',
            transition: 'all .2s', fontFamily: 'inherit'
          }}>
            {t.purchases}
          </button>
          <button onClick={() => setActiveTab('expiration')} style={{
            padding: '9px 18px', fontSize: 13, whiteSpace: 'nowrap',
            fontWeight: activeTab === 'expiration' ? 500 : 400,
            color: activeTab === 'expiration' ? '#111' : '#9A9890',
            border: activeTab === 'expiration' ? '0.5px solid #E8E6DF' : 'none',
            background: activeTab === 'expiration' ? '#fff' : 'transparent',
            borderRadius: 100, cursor: 'pointer', letterSpacing: '-0.1px',
            transition: 'all .2s', fontFamily: 'inherit'
          }}>
            {t.expiration}
          </button>
        </div>
      </div>

      {activeTab === 'purchases' && (
        <div>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ background: '#FFFFFF', borderRadius: '20px', padding: '20px', border: '0.5px solid #EAEAE4', display: 'flex', gap: '16px' }}>
                  <div style={{ width: '72px', height: '72px', borderRadius: '16px', background: '#F4F3EF', animation: 'pulse 1.5s infinite' }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                    <div style={{ width: '60%', height: '16px', background: '#F4F3EF', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
                    <div style={{ width: '40%', height: '14px', background: '#F4F3EF', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#FFFFFF', borderRadius: '20px', border: '0.5px solid #EAEAE4' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📦</div>
              <div style={{ fontSize: '16px', color: '#9A9890' }}>{t.noHistory}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {orders.map((order) => {
                const source = getSourceLogo(order.source)
                const bonusUsed = order.bonus_used || 0
                const originalAmount = bonusUsed > 0 ? (order.total_amount || 0) + bonusUsed : (order.total_amount || 0)
                const paidAmount = originalAmount - bonusUsed
                const bonusEarned = bonusUsed > 0 ? 0 : Math.floor(paidAmount * 0.05)
                return (
                  <div key={order.id} style={{
                    background: '#FFFFFF', borderRadius: '20px', padding: '20px',
                    border: '0.5px solid #EAEAE4', transition: 'all 0.2s ease'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ width: '72px', height: '72px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', flexShrink: 0 }}>
                        <img src={source.logo} alt={source.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable="false" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>{source.label}</div>
                        <div style={{ fontSize: '13px', color: '#9A9890', marginBottom: '4px' }}>{source.subtitle}</div>
                        <div style={{ fontSize: '13px', color: '#9A9890', marginBottom: '4px' }}>{t.purchaseAmount}: {originalAmount} {t.tenge}</div>
                        {bonusUsed > 0 && (
                          <div style={{ fontSize: '13px', color: '#EF4444', marginBottom: '4px' }}>
                            <div>{t.bonusUsed}:</div>
                            <div>-{bonusUsed} {t.bonus}</div>
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#B0ADA5' }}>{formatDate(order.created_at)}</div>
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        color: bonusUsed > 0 ? '#EF4444' : '#10B981', fontSize: '16px', fontWeight: '600'
                      }}>
                        {bonusUsed > 0 ? (<><span>-</span><span>{bonusUsed}</span></>) : (<><span>+</span><span>{bonusEarned}</span></>)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'expiration' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {expirations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#FFFFFF', borderRadius: '20px', border: '0.5px solid #EAEAE4' }}>
              <div style={{ marginBottom: '16px', opacity: 0.5, display: 'flex', justifyContent: 'center' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 7h12M6 11h12M12 11v10"/>
                </svg>
              </div>
              <div style={{ fontSize: '16px', color: '#9A9890' }}>{t.noExpiration}</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {expirations.map((exp, idx) => {
                  const dateObj = new Date(exp.expires_at)
                  const day = dateObj.getDate()
                  const monthName = getMonthName(dateObj.getMonth())
                  const year = dateObj.getFullYear()

                  return (
                    <div key={idx} style={{
                      background: '#FFFFFF', borderRadius: '16px', padding: '20px',
                      border: '0.5px solid #EAEAE4', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#FFF5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 7h12M6 11h12M12 11v10"/>
                          </svg>
                        </div>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>
                            {exp.amount} {bonusLabel}
                          </div>
                          <div style={{ fontSize: '13px', color: '#9A9890', marginTop: '2px' }}>
                            {renderExpiresText(day, monthName, year)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div style={{ textAlign: 'center', padding: '16px 16px 8px' }}>
                <span className="highlight-wave" style={{
                  fontWeight: '700', fontSize: '17px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                }}>
                  {expireSoonText}
                </span>
              </div>

              <div style={{ marginTop: '8px', padding: '0 8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111', marginBottom: '12px' }}>{termsTitle}</h3>
                <ul style={{ color: '#666', fontSize: '14px', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {termsList.map((term, i) => (
                    <li key={i}>{term[lang] || term['ru']}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}